import { useState, useEffect, useRef, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MdCreate, MdInbox, MdMarkEmailUnread, MdArchive, MdPushPin } from 'react-icons/md';
import api from '../../services/api';
import { getUser } from '../../services/auth';
import { t } from '../../utils/i18n';
import { useToast } from '../../components/ToastContainer';
import ToastContainer from '../../components/ToastContainer';
import ConfirmDialog from '../../components/ConfirmDialog';
import { initSocket } from '../../services/socket';
import { useDebounce } from '../../hooks/useDebounce';
import ConversationList from '../../components/chat/ConversationList';
import ChatThread from '../../components/chat/ChatThread';
import NewMessageModal from '../../components/chat/NewMessageModal';
import ChatSearchModal from '../../components/chat/ChatSearchModal';
import ProfileDialog from '../../components/chat/ProfileDialog';
import { getConnectionBadgeLabel } from '../../components/chat/connectionBadge';

export default function ConversationsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = getUser();
  const { showToast, removeToast, toasts } = useToast();

  const [conversations, setConversations] = useState([]);
  const [friends, setFriends] = useState([]);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [contactsSearchQuery, setContactsSearchQuery] = useState('');
  const debouncedSearch = useDebounce(searchQuery, 400);
  const debouncedContactsSearch = useDebounce(contactsSearchQuery, 300);
  const [filter, setFilter] = useState('all');
  const [activeTab, setActiveTab] = useState('conversations');
  const [pinnedIds, setPinnedIds] = useState(() => new Set());
  const [archivedIds, setArchivedIds] = useState(() => new Set());
  const [mutedIds, setMutedIds] = useState(() => new Set());
  const [showNewMessageModal, setShowNewMessageModal] = useState(false);
  const [showChatSearchModal, setShowChatSearchModal] = useState(false);
  const [replyTo, setReplyTo] = useState(null);
  const [showConversationView, setShowConversationView] = useState(false);
  const [inviteFriendDialog, setInviteFriendDialog] = useState({ isOpen: false, friend: null });
  const [confirmDialog, setConfirmDialog] = useState({ open: false, title: '', message: '', onConfirm: null, type: 'warning' });
  const [profileDialogUserId, setProfileDialogUserId] = useState(null);
  const [otherUserProfileCache, setOtherUserProfileCache] = useState({});

  const socketRef = useRef(null);
  const selectedConversationRef = useRef(null);

  useEffect(() => {
    selectedConversationRef.current = selectedConversation;
  }, [selectedConversation]);

  const getOtherUser = (conv) => {
    if (conv?.otherUser) return conv.otherUser;
    const participants = conv?.participants;
    if (!participants || !Array.isArray(participants)) return null;
    const otherId = participants.find((p) => (typeof p === 'object' ? p?.id : p) !== user?.id);
    return typeof otherId === 'object' ? otherId : (otherId != null ? { id: otherId } : null);
  };

  const friendsByUserId = useMemo(() => {
    const map = {};
    (friends || []).forEach((f) => {
      const id = f.friendUserId ?? f.id;
      if (id) map[id] = f;
    });
    return map;
  }, [friends]);

  const loadConversations = async (opts = {}) => {
    try {
      if (!opts.silent) setLoading(true);
      const res = await api.get('/conversations').catch(() => ({ data: [] }));
      setConversations(res.data || []);
    } catch (err) {
      console.error(err);
      if (!opts.silent) showToast(t('conversations.loadError') || 'Greška pri učitavanju.', 'error');
    } finally {
      if (!opts.silent) setLoading(false);
    }
  };

  const loadFriends = async () => {
    try {
      const res = await api.get('/friends').catch(() => ({ data: [] }));
      setFriends(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      console.error(err);
    }
  };

  const loadMessages = async (conversationId, opts = {}) => {
    try {
      setLoadingMessages(true);
      const res = await api.get(`/conversations/${conversationId}/messages`).catch(() => ({ data: [] }));
      setMessages(res.data || []);
      await api.put(`/conversations/${conversationId}/read`).catch(() => null);
      if (!opts.skipRefresh) loadConversations({ silent: true });
    } catch (err) {
      console.error(err);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    const socket = initSocket();
    socketRef.current = socket;
    if (!socket) return;
    const onNew = (data) => {
      if (selectedConversationRef.current?.id === data.conversationId) {
        setMessages((prev) => (prev.some((m) => m.id === data.id) ? prev : [...prev, data]));
      }
      loadConversations({ silent: true });
    };
    const onConvUpdated = ({ conversationId }) => {
      loadConversations({ silent: true });
      if (selectedConversationRef.current?.id === conversationId) {
        loadMessages(conversationId, { skipRefresh: true });
      }
    };
    const onMsgUpdated = (data) => {
      if (selectedConversationRef.current?.id === data.conversationId) {
        setMessages((prev) => prev.map((m) => (m.id === data.id ? { ...m, ...data } : m)));
      }
      loadConversations({ silent: true });
    };
    const onMsgDeleted = ({ conversationId, messageId }) => {
      if (selectedConversationRef.current?.id === conversationId) {
        setMessages((prev) => prev.filter((m) => String(m.id) !== String(messageId)));
      }
      loadConversations({ silent: true });
    };
    socket.on('new-message', onNew);
    socket.on('conversation-updated', onConvUpdated);
    socket.on('message-updated', onMsgUpdated);
    socket.on('message-deleted', onMsgDeleted);
    return () => {
      socket.off('new-message', onNew);
      socket.off('conversation-updated', onConvUpdated);
      socket.off('message-updated', onMsgUpdated);
      socket.off('message-deleted', onMsgDeleted);
    };
  }, []);

  useEffect(() => {
    loadConversations();
    loadFriends();
    return () => {
      if (socketRef.current) {
        socketRef.current.disconnect();
        socketRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const friendId = searchParams.get('friendId') || searchParams.get('userId');
    if (friendId) {
      handleStartConversation(parseInt(friendId, 10));
      setSearchParams({});
    }
  }, [searchParams.get('friendId'), searchParams.get('userId')]);

  useEffect(() => {
    if (selectedConversation && socketRef.current) {
      loadMessages(selectedConversation.id, { skipRefresh: true });
      socketRef.current.emit('join-conversation', selectedConversation.id);
      return () => socketRef.current?.emit('leave-conversation', selectedConversation.id);
    } else {
      setMessages([]);
    }
  }, [selectedConversation?.id]);

  // Fetch otherUser profile when incomplete (e.g. from newly created conversation)
  useEffect(() => {
    if (!selectedConversation) return;
    const ou = getOtherUser(selectedConversation);
    const otherId = ou?.id;
    if (!otherId || otherUserProfileCache[otherId]) return;
    if (ou?.photoUrl || ou?.photo_url) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await api.get(`/users/${otherId}`).catch(() => ({ data: null }));
        if (!cancelled && res?.data) {
          setOtherUserProfileCache((prev) => ({ ...prev, [otherId]: res.data }));
        }
      } catch (e) {
        console.error(e);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedConversation?.id, otherUserProfileCache]);

  const handleStartConversation = async (friendId, opts = {}) => {
    const keepContactsTab = opts.keepContactsTab === true;
    const friend = friends.find((f) => f.id === friendId || f.friendUserId === friendId);
    const otherId = friend ? (friend.friendUserId ?? friend.id) : friendId;

    // Check if we already have a conversation with this user (from friends or from URL)
    const existing = conversations.find((c) => getOtherUser(c)?.id === otherId);
    if (existing) {
      setSelectedConversation(existing);
      setShowConversationView(true);
      if (!keepContactsTab) setActiveTab('conversations');
      setShowNewMessageModal(false);
      return;
    }

    if (friend) {
      if (!friend.hasAccount) {
        showToast(t('conversations.noAccountMessage') || 'Prijatelj nema nalog. Ne možete slati poruke dok ne kreira nalog.', 'info');
        setInviteFriendDialog({ isOpen: true, friend });
        return;
      }
    } else {
      // User not in friends - try to create conversation anyway (backend may require friendship)
      try {
        const res = await api.post('/conversations', { participantId: otherId });
        const conv = res.data;
        setConversations((prev) => [conv, ...prev]);
        setSelectedConversation(conv);
        setShowConversationView(true);
        if (!keepContactsTab) setActiveTab('conversations');
        setShowNewMessageModal(false);
      } catch (err) {
        console.error(err);
        showToast(t('conversations.startError') || 'Greška pri pokretanju razgovora.', 'error');
      }
      return;
    }

    try {
      const res = await api.post('/conversations', { participantId: otherId });
      const conv = res.data;
      setConversations((prev) => [conv, ...prev]);
      setSelectedConversation(conv);
      setShowConversationView(true);
      if (!keepContactsTab) setActiveTab('conversations');
      setShowNewMessageModal(false);
    } catch (err) {
      console.error(err);
      showToast(t('conversations.startError') || 'Greška pri pokretanju razgovora.', 'error');
    }
  };

  const filteredConversations = useMemo(() => {
    let list = conversations.filter((c) => {
      if (filter === 'archived') return archivedIds.has(c.id);
      if (filter === 'pinned') return pinnedIds.has(c.id);
      if (filter === 'unread') return (c.unreadCount ?? 0) > 0;
      return !archivedIds.has(c.id);
    });
    if (debouncedSearch.trim()) {
      const q = debouncedSearch.trim().toLowerCase();
      list = list.filter((c) => {
        const other = getOtherUser(c);
        const name = other?.name || (other?.firstName && other?.lastName ? `${other.firstName} ${other.lastName}` : '') || other?.email || '';
        const last = c.lastMessage?.content || '';
        return name.toLowerCase().includes(q) || (other?.email || '').toLowerCase().includes(q) || last.toLowerCase().includes(q);
      });
    }
    return list.sort((a, b) => {
      const aTime = a.lastMessage?.createdAt || a.createdAt;
      const bTime = b.lastMessage?.createdAt || b.createdAt;
      return new Date(bTime || 0) - new Date(aTime || 0);
    });
  }, [conversations, filter, pinnedIds, archivedIds, debouncedSearch, getOtherUser]);

  const compressImage = (file, maxWidth = 800, quality = 0.7) => {
    return new Promise((resolve) => {
      const img = new Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        const canvas = document.createElement('canvas');
        let { width, height } = img;
        if (width > maxWidth) {
          height = (height * maxWidth) / width;
          width = maxWidth;
        }
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, width, height);
        const dataUrl = canvas.toDataURL('image/jpeg', quality);
        resolve(dataUrl);
      };
      img.onerror = () => {
        URL.revokeObjectURL(url);
        const reader = new FileReader();
        reader.onload = () => resolve(reader.result);
        reader.readAsDataURL(file);
      };
      img.src = url;
    });
  };

  const handleAttachmentSelect = async (file) => {
    if (!selectedConversation || !file) return;
    if (!file.type?.startsWith('image/')) {
      showToast('Izaberite sliku (JPG, PNG, GIF).', 'error');
      return;
    }
    try {
      const dataUrl = await compressImage(file);
      await handleSendMessage(dataUrl);
    } catch (err) {
      console.error(err);
      showToast(t('conversations.sendError') || 'Greška pri slanju.', 'error');
    }
  };

  const handleSendMessage = async (content, replyToMessage) => {
    if (!selectedConversation || !content) return;
    const trimmed = typeof content === 'string' ? content.trim() : content;
    if (!trimmed) return;
    const temp = {
      id: `temp-${Date.now()}`,
      content: trimmed,
      senderId: user.id,
      createdAt: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, temp]);
    setReplyTo(null);
    try {
      const res = await api.post(`/conversations/${selectedConversation.id}/messages`, { content: trimmed });
      const msg = res.data;
      setMessages((prev) => prev.map((m) => (m.id === temp.id ? msg : m)));
    } catch (err) {
      console.error(err);
      setMessages((prev) => prev.filter((m) => m.id !== temp.id));
      showToast(t('conversations.sendError') || 'Greška pri slanju.', 'error');
    }
  };

  const handleDeleteMessage = async (message) => {
    if (!selectedConversation || !message) return;
    try {
      await api.delete(`/conversations/${selectedConversation.id}/messages/${message.id}`);
      setMessages((prev) => prev.filter((m) => String(m.id) !== String(message.id)));
    } catch (err) {
      showToast(t('conversations.deleteMessageError') || 'Greška pri brisanju poruke.', 'error');
    }
  };

  const handlePin = (conv) => setPinnedIds((s) => (s.has(conv.id) ? new Set([...s].filter((id) => id !== conv.id)) : new Set([...s, conv.id])));
  const handleArchive = (conv) => setArchivedIds((s) => (s.has(conv.id) ? new Set([...s].filter((id) => id !== conv.id)) : new Set([...s, conv.id])));
  const handleMute = (conv) => {
    setMutedIds((s) => {
      const next = s.has(conv.id) ? new Set([...s].filter((id) => id !== conv.id)) : new Set([...s, conv.id]);
      const nowMuted = next.has(conv.id);
      showToast(nowMuted ? (t('conversations.muted') || 'Razgovor utišan') : (t('conversations.unmuted') || 'Uklonjeno utišavanje'), 'info');
      return next;
    });
  };

  const handleDeleteConversation = (conv) => {
    setConfirmDialog({
      open: true,
      title: t('conversations.deleteConfirmTitle') || 'Obriši razgovor?',
      message: t('conversations.deleteConfirm') || 'Da li ste sigurni da želite da obrišete ovaj razgovor?',
      type: 'danger',
      onConfirm: () => {
        setConversations((prev) => prev.filter((c) => c.id !== conv.id));
        if (selectedConversation?.id === conv.id) {
          setSelectedConversation(null);
          setShowConversationView(false);
        }
        setConfirmDialog((d) => ({ ...d, open: false }));
        showToast(t('conversations.deleteSuccess') || 'Razgovor je obrisan.', 'success');
      },
    });
  };

  const otherUser = selectedConversation ? getOtherUser(selectedConversation) : null;
  const otherId = otherUser?.id;
  const cachedProfile = otherId ? otherUserProfileCache[otherId] : null;
  const displayUser = cachedProfile || otherUser;
  const otherName = displayUser?.name || (displayUser?.firstName && displayUser?.lastName ? `${displayUser.firstName} ${displayUser.lastName}` : null) || displayUser?.email || 'Korisnik';
  const otherPhoto = displayUser?.photoUrl || displayUser?.photo_url || displayUser?.profileImage;
  const otherSecondary = displayUser?.email || (displayUser?.address?.city ? displayUser.address.city : '');
  const connectionBadge = otherUser ? getConnectionBadgeLabel(friendsByUserId[otherUser.id]?.relationshipType) : 'Ostalo';

  return (
    <div className="mx-auto w-full max-w-[1400px] px-4 lg:px-6 pt-6 h-[calc(100vh-6rem-1.5rem)] min-h-0 flex flex-col lg:grid lg:grid-cols-[30%_70%] lg:gap-4 bg-gray-50 dark:bg-gray-900/50">
      {/* LEFT PANEL: Narrow (1) — tabs, search, Nova poruka, status, contacts list */}
      <div
        className={`
          flex flex-col min-h-0 flex-1 lg:flex-initial rounded-2xl bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden
          ${showConversationView ? 'hidden lg:flex' : 'flex'}
        `}
      >
        <div className="flex-shrink-0 p-4 border-b border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
          {/* Tabs */}
          <div className="flex border-b border-gray-200 dark:border-gray-700 mb-3">
            <button
              type="button"
              onClick={() => setActiveTab('conversations')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium border-b-2 ${
                activeTab === 'conversations' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400'
              }`}
            >
              Razgovori
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('contacts')}
              className={`flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium border-b-2 ${
                activeTab === 'contacts' ? 'border-primary-500 text-primary-600 dark:text-primary-400' : 'border-transparent text-gray-500 dark:text-gray-400'
              }`}
            >
              Kontakti
            </button>
          </div>
          {activeTab === 'conversations' && (
            <>
              {/* Pretraži razgovore — above Nova poruka */}
              <div className="relative mb-3">
                <input
                  type="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Pretraži razgovore..."
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">⌕</span>
              </div>
              <button
                type="button"
                onClick={() => setShowNewMessageModal(true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold mb-3"
              >
                <MdCreate className="text-lg" />
                Nova poruka
              </button>
              {/* Mailbox-style status items with icons */}
              <div className="space-y-0.5 mb-3">
                {[
                  { value: 'all', label: 'Svi', icon: MdInbox },
                  { value: 'unread', label: 'Nepročitani', icon: MdMarkEmailUnread },
                  { value: 'archived', label: 'Arhivirani', icon: MdArchive },
                  { value: 'pinned', label: 'Označeni', icon: MdPushPin },
                ].map(({ value, label, icon: Icon }) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => {
                      setFilter(value);
                      setSelectedConversation(null);
                      setShowConversationView(false);
                    }}
                    className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left text-sm font-medium transition-colors ${
                      filter === value
                        ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700/50'
                    }`}
                  >
                    <Icon className="text-xl flex-shrink-0 text-gray-500 dark:text-gray-400" />
                    {label}
                  </button>
                ))}
              </div>
            </>
          )}
          {activeTab === 'contacts' && (
            <>
              <div className="relative mb-3">
                <input
                  type="search"
                  value={contactsSearchQuery}
                  onChange={(e) => setContactsSearchQuery(e.target.value)}
                  placeholder="Pretraži kontakte..."
                  className="w-full pl-9 pr-3 py-2 text-sm rounded-xl border border-gray-200 dark:border-gray-600 bg-gray-50 dark:bg-gray-700/50"
                />
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none">⌕</span>
              </div>
              <button
                type="button"
                onClick={() => setShowNewMessageModal(true)}
                className="w-full flex items-center justify-center gap-2 py-2.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold mb-3"
              >
                <MdCreate className="text-lg" />
                Novi kontakt
              </button>
            </>
          )}
        </div>
        {/* List area: contacts when contacts tab */}
        {activeTab === 'contacts' && (
          <div className="flex-1 min-h-0 overflow-y-auto p-2">
            {friends.length === 0 ? (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400 text-sm">
                Nema kontakata. <button type="button" onClick={() => navigate('/dashboard/friends')} className="text-primary-600 dark:text-primary-400 font-medium">Dodaj prijatelje</button>.
              </div>
            ) : (
              <ul className="space-y-1">
                {friends
                  .filter((friend) => {
                    if (!debouncedContactsSearch.trim()) return true;
                    const q = debouncedContactsSearch.trim().toLowerCase();
                    const name = friend.name || `${(friend.firstName || '')} ${(friend.lastName || '')}`.trim() || friend.manualName || friend.email || '';
                    return name.toLowerCase().includes(q) || (friend.email || '').toLowerCase().includes(q);
                  })
                  .map((friend) => {
                  const name = friend.name || `${(friend.firstName || '')} ${(friend.lastName || '')}`.trim() || friend.manualName || 'Nepoznat';
                  const existingConv = conversations.find((c) => getOtherUser(c)?.id === (friend.friendUserId ?? friend.id));
                  return (
                    <li key={friend.id}>
                      <button
                        type="button"
                        onClick={() => {
                          if (existingConv) {
                            setSelectedConversation(existingConv);
                            setShowConversationView(true);
                          } else {
                            handleStartConversation(friend.friendUserId ?? friend.id, { keepContactsTab: true });
                          }
                        }}
                        className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-gray-50 dark:hover:bg-gray-700/50 text-left border border-gray-200 dark:border-gray-600 focus:ring-2 focus:ring-primary-500"
                      >
                        <div className="w-10 h-10 rounded-full bg-primary-100 dark:bg-primary-900/40 flex items-center justify-center text-primary-600 dark:text-primary-400 font-semibold text-sm overflow-hidden flex-shrink-0">
                          {(friend.photoUrl || friend.photo_url) ? (
                            <img src={friend.photoUrl || friend.photo_url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            name[0]?.toUpperCase() || '?'
                          )}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{name}</p>
                          {friend.email && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{friend.email}</p>}
                        </div>
                        <span className="text-xs font-medium text-primary-600 dark:text-primary-400">Poruka</span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>

      {/* RIGHT PANEL: Conversations list or chat — wider (2) */}
      <div className="flex flex-col min-h-0 flex-1 rounded-2xl bg-white dark:bg-gray-800 shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        {selectedConversation ? (
          <ChatThread
            conversation={selectedConversation}
            messages={messages}
            currentUserId={user?.id}
            otherUserDisplayName={otherName}
            otherUserSecondary={otherSecondary}
            otherUserPhotoUrl={otherPhoto}
            connectionBadgeLabel={connectionBadge}
            loadingMessages={loadingMessages}
            replyTo={replyTo}
            onSendMessage={handleSendMessage}
            onReply={setReplyTo}
            onCancelReply={() => setReplyTo(null)}
            onDeleteMessage={handleDeleteMessage}
            onBack={() => {
              setShowConversationView(false);
              setSelectedConversation(null);
            }}
            showBackButton={true}
            isMuted={selectedConversation && mutedIds.has(selectedConversation.id)}
            onMute={() => selectedConversation && handleMute(selectedConversation)}
            onArchive={() => selectedConversation && handleArchive(selectedConversation)}
            onBlockOrReport={() => setConfirmDialog({ open: true, title: 'Blokiraj / Prijavi', message: 'Funkcija će biti dostupna uskoro.', type: 'info', onConfirm: () => setConfirmDialog((d) => ({ ...d, open: false })) })}
            onClearChat={() => selectedConversation && handleDeleteConversation(selectedConversation)}
            onViewProfile={() => otherUser?.id && setProfileDialogUserId(otherUser.id)}
            onSearchInChat={() => setShowChatSearchModal(true)}
            onAttachmentClick={handleAttachmentSelect}
            t={t}
          />
        ) : activeTab === 'contacts' && !selectedConversation ? (
          <div className="flex-1 min-h-0 flex flex-col items-center justify-center p-8 text-center">
            <p className="text-gray-500 dark:text-gray-400 text-sm">
              Izaberite kontakt u levom delu da otvorite razgovor.
            </p>
          </div>
        ) : (
          <div className="flex-1 min-h-0 overflow-hidden flex flex-col">
            <ConversationList
              conversations={filteredConversations}
              friendsByUserId={friendsByUserId}
              currentUserId={user?.id}
              selectedConversationId={selectedConversation?.id}
              pinnedIds={pinnedIds}
              archivedIds={archivedIds}
              mutedIds={mutedIds}
              onSelectConversation={(conv) => {
                setSelectedConversation(conv);
                setShowConversationView(true);
              }}
              onPin={handlePin}
              onArchive={handleArchive}
              onMute={handleMute}
              onDelete={handleDeleteConversation}
              onMarkRead={() => {}}
              onNewMessage={() => setShowNewMessageModal(true)}
              loading={loading}
              error={null}
              onRetry={() => loadConversations()}
              emptyMessage={t('conversations.noConversations') || 'Nema razgovora'}
              searchEmptyMessage={t('conversations.noResults') || 'Nema rezultata'}
              hasSearchQuery={!!debouncedSearch.trim()}
              t={t}
            />
          </div>
        )}
      </div>

      {/* Mobile: FAB for new message */}
      <div className="lg:hidden fixed bottom-6 right-6 z-40">
        <button
          type="button"
          onClick={() => setShowNewMessageModal(true)}
          className="w-14 h-14 rounded-full bg-primary-500 text-white shadow-lg flex items-center justify-center hover:bg-primary-600 focus:ring-2 focus:ring-primary-500"
          aria-label="Nova poruka"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24"><path d="M20 2H4c-1.1 0-2 .9-2 2v18l4-4h14c1.1 0 2-.9 2-2V4c0-1.1-.9-2-2-2z"/></svg>
        </button>
      </div>

      <ChatSearchModal
        isOpen={showChatSearchModal}
        onClose={() => setShowChatSearchModal(false)}
        messages={messages}
        currentUserId={user?.id}
        senderDisplayName={otherName}
        onScrollToMessage={() => setShowChatSearchModal(false)}
        t={t}
      />

      <NewMessageModal
        isOpen={showNewMessageModal}
        onClose={() => setShowNewMessageModal(false)}
        friends={friends}
        existingConversations={conversations}
        getOtherUser={getOtherUser}
        onSelectContact={(friend, existingConv) => {
          if (existingConv) {
            setSelectedConversation(existingConv);
            setShowConversationView(true);
            setActiveTab('conversations');
          } else {
            handleStartConversation(friend.friendUserId ?? friend.id);
          }
        }}
        t={t}
      />

      {inviteFriendDialog.isOpen && inviteFriendDialog.friend && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl max-w-md w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">{t('friends.inviteFriend') || 'Pozovi prijatelja'}</h3>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">{t('conversations.noAccountMessage') || 'Prijatelj nema nalog. Ne možete slati poruke dok ne kreira nalog.'}</p>
            <div className="flex gap-3">
              <button type="button" onClick={() => setInviteFriendDialog({ isOpen: false, friend: null })} className="flex-1 px-4 py-2.5 rounded-xl border border-gray-200 dark:border-gray-600 text-gray-700 dark:text-gray-300">
                {t('common.cancel') || 'Otkaži'}
              </button>
              <button
                type="button"
                onClick={async () => {
                  const f = inviteFriendDialog.friend;
                  try {
                    await api.post('/friends/invite-manual', { email: f.email, name: f.name || f.manualName, relationshipType: f.relationshipType || 'babysitter' });
                    showToast(t('friends.inviteSent') || 'Pozivnica poslata!', 'success');
                    setInviteFriendDialog({ isOpen: false, friend: null });
                  } catch (err) {
                    showToast(t('friends.inviteError') || 'Greška pri slanju.', 'error');
                  }
                }}
                className="flex-1 px-4 py-2.5 rounded-xl bg-primary-500 text-white font-medium"
              >
                {t('friends.sendInvitation') || 'Pošalji poziv'}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.open}
        onClose={() => setConfirmDialog((d) => ({ ...d, open: false }))}
        onConfirm={confirmDialog.onConfirm || (() => setConfirmDialog((d) => ({ ...d, open: false })))}
        title={confirmDialog.title}
        message={confirmDialog.message}
        type={confirmDialog.type}
        confirmText="Potvrdi"
        cancelText="Otkaži"
      />

      <ToastContainer toasts={toasts} removeToast={removeToast} />

      {profileDialogUserId && (
        <ProfileDialog
          userId={profileDialogUserId}
          onClose={() => setProfileDialogUserId(null)}
        />
      )}
    </div>
  );
}
